/* Tormeister24 cart sharing v1.0.0 — 2026-10-14.
 * Read-only Ajax cart snapshot -> Shopify cart permalink. No cart token,
 * customer data, prices, notes, discounts, or tracking attributes are shared.
 * Configured/subscription/bundle lines fail closed instead of losing options.
 */
(function () {
  'use strict';

  function fail(code) { throw new Error(code); }
  function positiveInteger(value) {
    if (typeof value === 'string' && !/^[1-9]\d*$/.test(value)) fail('invalid');
    if (typeof value !== 'string' && typeof value !== 'number') fail('invalid');
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < 1) fail('invalid');
    return number;
  }
  function buildCartLink(cart, origin, cartPath) {
    if (!cart || !Array.isArray(cart.items)) fail('invalid');
    if (!cart.items.length) fail('empty');
    const base = new URL(origin);
    if (!/^https?:$/.test(base.protocol) || base.username || base.password) fail('invalid');
    if (typeof cartPath !== 'string' || !/^\/(?:[a-zA-Z0-9_-]+\/)*cart\/?$/.test(cartPath)) fail('invalid');
    const lines = new Map();
    let count = 0;
    for (const item of cart.items) {
      if (!item || typeof item !== 'object') fail('invalid');
      if ((item.properties && Object.keys(item.properties).length) ||
          item.selling_plan_allocation || item.selling_plan || item.has_components ||
          item.parent_relationship || (item.item_components && item.item_components.length)) fail('configured');
      const rawId = item.variant_id;
      if (typeof rawId === 'number' && !Number.isSafeInteger(rawId)) fail('invalid');
      if (!['number', 'string'].includes(typeof rawId)) fail('invalid');
      const id = String(rawId);
      if (!/^[1-9]\d{0,19}$/.test(id)) fail('invalid');
      const quantity = positiveInteger(item.quantity);
      const total = (lines.get(id) || 0) + quantity;
      count += quantity;
      if (!Number.isSafeInteger(total) || !Number.isSafeInteger(count)) fail('invalid');
      lines.set(id, total);
    }
    const url = new URL(cartPath.replace(/\/$/, '') + '/' +
      Array.from(lines, ([id, quantity]) => id + ':' + quantity).join(','), base.origin);
    url.searchParams.set('storefront', 'true');
    // Deliberate compatibility limit for copy, mail clients and messengers.
    if (url.href.length > 2000) fail('long');
    return { url: url.href, count: count, variants: lines.size };
  }

  // The same production serializer is covered by the supplied Node tests.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildCartLink: buildCartLink };
    return;
  }
  if (!window.customElements || customElements.get('tm24-cart-share')) return;
  const config = document.getElementById('tm24-cart-share-config');
  if (!config) return;
  const language = (config.dataset.locale || document.documentElement.lang || 'de').toLowerCase().split('-')[0];
  const translations = {
    de: {
      title: 'Warenkorb teilen', intro: 'Artikelauswahl mit Handwerkern, Kollegen oder Familie abstimmen.',
      loading: 'Aktuellen Warenkorb laden …', ready: '{variants} Varianten · {count} Stück im Link',
      copy: 'Link kopieren', copied: 'Link kopiert.', manual: 'Bitte den markierten Link kopieren.',
      label: 'Link zu Ihrer Artikelauswahl', email: 'E-Mail', native: 'Weitere Optionen', refresh: 'Link neu erstellen',
      privacy: 'Nur Artikel, Varianten und Mengen werden geteilt. Keine Kundendaten, Bestellnotizen oder Gutscheincodes.',
      notice: 'Der Link ist eine Momentaufnahme. Preise und Verfügbarkeit können sich ändern. Ein vorhandener Warenkorb beim Empfänger kann ersetzt werden.',
      message: 'Meine Artikelauswahl bei Tormeister24. Bitte Artikel und Mengen vor der Bestellung prüfen. Ein vorhandener Warenkorb kann ersetzt werden.',
      subject: 'Artikelauswahl bei Tormeister24', empty: 'Ihr Warenkorb ist leer. Legen Sie zuerst Artikel hinein.',
      configured: 'Dieser Warenkorb enthält Sonderangaben, ein Abo oder verknüpfte Set-Artikel. Damit nichts verloren geht, kann dafür kein Standardlink erstellt werden. Bitte stimmen Sie diese Auswahl direkt mit uns ab.',
      invalid: 'Der Warenkorb konnte nicht eindeutig gelesen werden. Bitte laden Sie die Seite neu.',
      long: 'Für diesen großen Warenkorb ist der Link zu lang. Bitte teilen Sie eine kleinere Auswahl.',
      error: 'Der Warenkorb konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
      changed: 'Der Warenkorb wurde geändert. Bitte erstellen Sie den Link neu.'
    },
    nl: {
      title: 'Winkelwagen delen', intro: 'Stem uw productkeuze af met een vakman, collega of familielid.',
      loading: 'Huidige winkelwagen laden …', ready: '{variants} varianten · {count} stuks in de link',
      copy: 'Link kopiëren', copied: 'Link gekopieerd.', manual: 'Kopieer de geselecteerde link.',
      label: 'Link naar uw productkeuze', email: 'E-mail', native: 'Meer opties', refresh: 'Nieuwe link maken',
      privacy: 'Alleen artikelen, varianten en aantallen worden gedeeld. Geen klantgegevens, bestelnotities of kortingscodes.',
      notice: 'De link is een momentopname. Prijzen en beschikbaarheid kunnen wijzigen. Een bestaande winkelwagen van de ontvanger kan worden vervangen.',
      message: 'Mijn productkeuze bij Tormeister24. Controleer artikelen en aantallen vóór het bestellen. Een bestaande winkelwagen kan worden vervangen.',
      subject: 'Productkeuze bij Tormeister24', empty: 'Uw winkelwagen is leeg. Voeg eerst artikelen toe.',
      configured: 'Deze winkelwagen bevat speciale gegevens, een abonnement of gekoppelde setartikelen. Om niets te verliezen, kan hiervoor geen standaardlink worden gemaakt. Stem deze keuze rechtstreeks met ons af.',
      invalid: 'De winkelwagen kon niet correct worden gelezen. Laad de pagina opnieuw.',
      long: 'De link voor deze winkelwagen is te lang. Deel een kleinere selectie.',
      error: 'De winkelwagen kon niet worden geladen. Probeer het opnieuw.', changed: 'De winkelwagen is gewijzigd. Maak een nieuwe link.'
    },
    fr: {
      title: 'Partager le panier', intro: 'Validez votre sélection avec un artisan, un collègue ou un proche.',
      loading: 'Chargement du panier actuel…', ready: '{variants} variantes · {count} articles dans le lien',
      copy: 'Copier le lien', copied: 'Lien copié.', manual: 'Veuillez copier le lien sélectionné.',
      label: 'Lien vers votre sélection', email: 'E-mail', native: 'Autres options', refresh: 'Créer un nouveau lien',
      privacy: 'Seuls les articles, les variantes et les quantités sont partagés. Aucune donnée client, note de commande ni code de réduction.',
      notice: 'Le lien reflète la sélection actuelle. Les prix et la disponibilité peuvent changer. Un panier existant du destinataire peut être remplacé.',
      message: 'Ma sélection chez Tormeister24. Vérifiez les articles et les quantités avant de commander. Un panier existant peut être remplacé.',
      subject: 'Sélection chez Tormeister24', empty: 'Votre panier est vide. Ajoutez des articles avant de le partager.',
      configured: 'Ce panier contient des informations spécifiques, un abonnement ou des articles liés. Pour éviter toute perte, aucun lien standard ne peut être créé. Contactez-nous pour valider cette sélection.',
      invalid: 'Le panier ne peut pas être lu correctement. Rechargez la page.',
      long: 'Le lien de ce panier est trop long. Partagez une sélection plus petite.',
      error: 'Impossible de charger le panier. Veuillez réessayer.', changed: 'Le panier a changé. Créez un nouveau lien.'
    },
    en: {
      title: 'Share cart', intro: 'Discuss your selection with an installer, colleague or family member.',
      loading: 'Loading current cart…', ready: '{variants} variants · {count} items in the link',
      copy: 'Copy link', copied: 'Link copied.', manual: 'Please copy the selected link.',
      label: 'Link to your selection', email: 'Email', native: 'More options', refresh: 'Create new link',
      privacy: 'Only products, variants and quantities are shared. No customer details, order notes or discount codes.',
      notice: 'The link is a snapshot. Prices and availability may change. The recipient’s existing cart may be replaced.',
      message: 'My selection at Tormeister24. Please check products and quantities before ordering. An existing cart may be replaced.',
      subject: 'Product selection at Tormeister24', empty: 'Your cart is empty. Add some products first.',
      configured: 'This cart contains custom details, a subscription or linked bundle items. A standard link cannot preserve these details. Please contact us to discuss this selection.',
      invalid: 'The cart could not be read correctly. Please reload the page.',
      long: 'This cart link is too long. Please share a smaller selection.',
      error: 'The cart could not be loaded. Please try again.', changed: 'The cart has changed. Please create a new link.'
    }
  };
  const text = translations[language] || translations.en;
  const instances = new Set();
  let revision = 0;
  let lastChange = 0;
  let uniqueId = 0;
  let frame = 0;
  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

  function invalidateAll() {
    revision += 1;
    lastChange = Date.now();
    instances.forEach(instance => instance.invalidate());
  }
  async function waitForCart() {
    const deadline = Date.now() + 8000;
    while (Date.now() - lastChange < 450 || document.querySelector('cart-items.pointer-events-none')) {
      if (Date.now() > deadline) fail('changed');
      await delay(100);
    }
  }
  function assertQuantities(cart, scope) {
    const lines = new Map(cart.items.map(item => [item.key, item.quantity]));
    scope.querySelectorAll('cart-items input[name="updates[]"][data-index]').forEach(input => {
      if (!input.checkValidity() || !lines.has(input.dataset.index) ||
          Number(input.value) !== Number(lines.get(input.dataset.index))) fail('changed');
    });
  }

  class TM24CartShare extends HTMLElement {
    connectedCallback() {
      instances.add(this);
      if (this.initialized) return;
      this.initialized = true;
      this.innerHTML = '<details class="tm24-share"><summary class="tm24-share__toggle"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true" focusable="false"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 10.5 6.8-4m-6.8 7 6.8 4"/></svg><span data-text="title"></span><span class="tm24-share__chevron" aria-hidden="true">+</span></summary><div class="tm24-share__panel"><p data-text="intro"></p><p class="tm24-share__status" role="status" aria-live="polite" aria-atomic="true"></p><div class="tm24-share__result" hidden><label class="tm24-share__label" data-text="label"></label><input class="tm24-share__url" type="text" readonly spellcheck="false" autocapitalize="off" autocomplete="off" dir="ltr"><div class="tm24-share__actions"><button type="button" data-action="copy" data-text="copy"></button><a data-action="whatsapp" target="_blank" rel="noopener noreferrer" referrerpolicy="no-referrer" data-no-instant>WhatsApp</a><a data-action="email" data-text="email" data-no-instant></a><button type="button" data-action="native" data-text="native" hidden></button></div></div><button class="tm24-share__refresh" type="button" data-action="refresh" data-text="refresh" hidden></button><p class="tm24-share__fine" data-text="privacy"></p><p class="tm24-share__fine" data-text="notice"></p></div></details>';
      this.querySelectorAll('[data-text]').forEach(node => { node.textContent = text[node.dataset.text]; });
      this.details = this.querySelector('details');
      this.status = this.querySelector('[role="status"]');
      this.result = this.querySelector('.tm24-share__result');
      this.input = this.querySelector('input');
      this.refresh = this.querySelector('[data-action="refresh"]');
      const id = 'tm24-share-link-' + (++uniqueId);
      this.input.id = id;
      this.querySelector('label').htmlFor = id;
      this.details.addEventListener('toggle', () => {
        if (this.details.open) this.prepare();
        else { this.invalidate(); this.status.textContent = ''; }
      });
      this.addEventListener('click', event => this.onClick(event));
      this.input.addEventListener('click', () => this.input.select());
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.details.open) {
          event.preventDefault();
          event.stopPropagation();
          this.details.open = false;
          this.querySelector('summary').focus();
        }
      });
    }
    disconnectedCallback() {
      instances.delete(this);
      this.controller?.abort();
      this.url = '';
    }
    invalidate() {
      this.requestId = (this.requestId || 0) + 1;
      this.controller?.abort();
      this.url = '';
      if (!this.initialized) return;
      this.input.value = '';
      this.result.hidden = true;
      this.querySelectorAll('a[data-action]').forEach(link => link.removeAttribute('href'));
      this.removeAttribute('aria-busy');
      this.refresh.disabled = false;
      this.refresh.hidden = !this.details.open;
      if (this.details.open) this.status.textContent = text.changed;
    }
    async prepare() {
      this.invalidate();
      const requestId = this.requestId;
      const startedRevision = revision;
      this.setAttribute('aria-busy', 'true');
      this.refresh.hidden = true;
      this.status.textContent = text.loading;
      let timeout;
      try {
        await waitForCart();
        if (!this.isConnected || this.requestId !== requestId) return;
        this.controller = new AbortController();
        timeout = setTimeout(() => this.controller.abort(), 15000);
        const root = config.dataset.rootUrl || '/';
        const endpoint = new URL(root.replace(/\/$/, '') + '/cart.js', location.origin);
        if (endpoint.origin !== location.origin) fail('invalid');
        const response = await fetch(endpoint.href, {
          method: 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: { Accept: 'application/json' }, signal: this.controller.signal
        });
        if (!response.ok) fail('error');
        const cart = await response.json();
        if (!this.isConnected || this.requestId !== requestId) return;
        if (revision !== startedRevision) fail('changed');
        // Visitor theme previews have a temporary host; never share that host.
        const shareOrigin = /(^|\.)shopifypreview\.com$/i.test(new URL(location.origin).hostname)
          ? new URL(config.dataset.shopUrl).origin : location.origin;
        const snapshot = buildCartLink(cart, shareOrigin, config.dataset.cartUrl || '/cart');
        assertQuantities(cart, this.closest('main-cart, cart-drawer'));
        this.url = snapshot.url;
        this.input.value = snapshot.url;
        const message = text.message + '\n' + snapshot.url;
        this.querySelector('[data-action="whatsapp"]').href = 'https://wa.me/?text=' + encodeURIComponent(message);
        this.querySelector('[data-action="email"]').href = 'mailto:?subject=' + encodeURIComponent(text.subject) + '&body=' + encodeURIComponent(message);
        this.querySelector('[data-action="native"]').hidden = typeof navigator.share !== 'function';
        this.status.textContent = text.ready.replace('{variants}', snapshot.variants).replace('{count}', snapshot.count);
        this.result.hidden = false;
        this.refresh.hidden = false;
      } catch (error) {
        if (this.isConnected && this.requestId === requestId) {
          this.status.textContent = text[error.message] || text.error;
          this.refresh.hidden = false;
        }
      } finally {
        clearTimeout(timeout);
        if (this.requestId === requestId) this.removeAttribute('aria-busy');
      }
    }
    async onClick(event) {
      const control = event.target.closest('[data-action]');
      if (!control || !this.contains(control)) return;
      const action = control.dataset.action;
      if (action === 'refresh') { event.preventDefault(); this.prepare(); return; }
      if (!this.url) { event.preventDefault(); return; }
      if (action === 'whatsapp' || action === 'email') return;
      event.preventDefault();
      const url = this.url;
      try {
        if (action === 'native' && typeof navigator.share === 'function') {
          // Called directly in this second click, retaining mobile user activation.
          await navigator.share({ title: text.subject, text: text.message, url: url });
        } else if (action === 'copy') {
          if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('clipboard');
          await navigator.clipboard.writeText(url);
          if (this.isConnected && this.url === url) this.status.textContent = text.copied;
        }
      } catch (error) {
        if (error.name === 'AbortError' || !this.isConnected || this.url !== url) return;
        this.input.focus();
        this.input.select();
        this.status.textContent = text.manual;
      }
    }
  }
  customElements.define('tm24-cart-share', TM24CartShare);

  const observedSections = new WeakSet();
  function mountAll() {
    frame = 0;
    document.querySelectorAll('main-cart, cart-drawer').forEach(scope => {
      const section = scope.closest('.shopify-section') || scope.parentElement;
      if (section && !observedSections.has(section)) {
        observedSections.add(section);
        new MutationObserver(records => {
          if (records.some(record => !record.target.closest?.('tm24-cart-share'))) scheduleMount();
        }).observe(section, { childList: true, subtree: true });
      }
      const cartItems = scope.querySelector('cart-items');
      const hasItems = cartItems && cartItems.querySelector('[id^="CartItem-"], [id^="CartDrawer-Item-"]');
      const existing = scope.querySelector('tm24-cart-share');
      if (!hasItems) { existing?.remove(); return; }
      if (existing) return;
      const widget = document.createElement('tm24-cart-share');
      if (scope.matches('cart-drawer')) {
        // Inside the scrollable area, not the fixed footer: checkout stays visible.
        cartItems.insertAdjacentElement('afterend', widget);
      } else {
        const buttons = scope.querySelector('.cart__sidebar button[name="checkout"]')?.parentElement;
        if (buttons) buttons.insertAdjacentElement('afterend', widget);
        else scope.querySelector('.cart__sidebar')?.append(widget);
      }
    });
  }
  function scheduleMount() {
    if (!frame) frame = requestAnimationFrame(mountAll);
  }
  ['input', 'change'].forEach(type => document.addEventListener(type, event => {
    if (event.target.matches?.('cart-items input[name="updates[]"]')) invalidateAll();
  }, true));
  document.addEventListener('click', event => {
    if (event.target.closest?.('cart-items a[data-index], cart-items button[name="plus"], cart-items button[name="minus"]')) invalidateAll();
  }, true);
  document.addEventListener('cart:updated', () => { invalidateAll(); scheduleMount(); });
  document.addEventListener('cart:refresh', () => { invalidateAll(); scheduleMount(); });
  document.addEventListener('shopify:section:load', scheduleMount);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) invalidateAll(); });
  window.addEventListener('pageshow', event => { if (event.persisted) invalidateAll(); });
  mountAll();
})();
